import { EMPTY_FIELD } from 'constants/emptyField';
import { Resources } from 'constants/resources';
import { READ_ONLY_ROLES } from 'constants/roles';

import { useState } from 'react';

import ContentAdd from '@mui/icons-material/Add';
import DataObjectIcon from '@mui/icons-material/DataObject';
import { usePermissions } from 'hooks/usePermissions';
import { useRecordModal } from 'hooks/useRecordModal';
import { MerchantsShowActionSkeleton } from 'pages/Merchants/MerchantsShow/components/MerchantsShowActionSkeleton';
import { MerchantsShowFiltersSkeleton } from 'pages/Merchants/MerchantsShow/components/MerchantsShowFiltersSkeleton';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { RecordContextProvider, useListController, WithListContext } from 'react-admin';
import { Alert } from 'shared/mui/Alert/Alert';
import { Box } from 'shared/mui/Box';
import { Button } from 'shared/mui/Buttons';
import { CardActionsButtons } from 'shared/mui/Card';
import {
  ContextMenu,
  ContextMenuDeleteButton,
  ContextMenuEditDialogButton,
  ContextMenuEntityUserActionsButton,
  ContextMenuShowDialogButton,
} from 'shared/mui/ContextMenu';
import { TooltipOverflow } from 'shared/mui/Tooltip/TooltipOverflow/TooltipOverflow';
import { DatagridSkeleton } from 'shared/react-admin/Datagrids';
import { DatagridStickyContainer } from 'shared/react-admin/Datagrids/DatagridSticky/DatagridStickyContainer';
import { LastActiveField } from 'shared/react-admin/Fields';
import { DateTimeField, FunctionField, TextField } from 'shared/react-admin/Fields';
import { EmbeddedArrayField } from 'shared/react-admin/Fields/Embedded/EmbeddedArrayField/EmbeddedArrayField';
import { ListPage } from 'shared/react-admin/Pages';
import { ReferenceField } from 'shared/react-admin/ReferenceFields';
import { AccountsCredentialsDialog } from 'shared/widgets/AccountsCredentialsDialog/AccountsCredentialsDialog';
import { GatewayShowDialog } from 'shared/widgets/GatewayShowDialog/GatewayShowDialog';
import { ProviderShowDialog } from 'shared/widgets/ProviderShowDialog/ProviderShowDialog';
import { Gateway } from 'types/embedded/gateway';
import { MerchantAccount } from 'types/embedded/merchantAccount';
import { Permission } from 'types/embedded/permission';
import { Provider } from 'types/embedded/provider';

import { MerchantAccountsNameFieldActiveSwitch } from './components/MerchantShowAccountsNameField/MerchantAccountsNameFieldActiveSwitch';
import { MerchantShowAccountsNameField } from './components/MerchantShowAccountsNameField/MerchantShowAccountsNameField';
import {
  ChipFieldLinkStyled,
  DatagridStyled,
  TextFieldStyled,
} from './MerchantShowAccounts.styled';
import { MerchantShowAccountsCreateDialog } from './MerchantShowAccountsCreateDialog/MerchantShowAccountsCreateDialog';
import { MerchantShowAccountsDeleteDialog } from './MerchantShowAccountsDeleteDialog/MerchantShowAccountsDeleteDialog';
import { MerchantShowAccountsEditDialog } from './MerchantShowAccountsEditDialog/MerchantShowAccountsEditDialog';
import { MerchantShowAccountsFilters } from './MerchantShowAccountsFilters/MerchantShowAccountsFilters';
import { MerchantShowAccountsShowDialog } from './MerchantShowAccountsShowDialog/MerchantShowAccountsShowDialog';

type Props = {
  merchantId?: string;
  merchantName?: string;
  merchantPlatformId?: string;
};

export const MerchantShowAccounts: React.FC<Props> = ({
  merchantId,
  merchantName,
  merchantPlatformId,
}) => {
  const translate = useTypedTranslate();
  const { isLoading } = useListController();

  const { hasResourcePermissions, hasResourceAccess, hasAnyRole } = usePermissions();

  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [returnDialog, setReturnDialog] = useState('');

  const editModal = useRecordModal<MerchantAccount>();
  const credentialsShowModal = useRecordModal<MerchantAccount>();
  const credentialsListModal = useRecordModal<MerchantAccount>();
  const showModal = useRecordModal<MerchantAccount>();
  const deleteModal = useRecordModal<MerchantAccount>();
  const providerModal = useRecordModal<Provider>();
  const gatewayModal = useRecordModal<Gateway>();

  const closeDeleteDialog = (record?: MerchantAccount) => {
    deleteModal.handleCloseRecordModal();
    setReturnDialog('');
    if (!record) {
      return;
    }
    if (returnDialog === 'show') {
      showModal.handleOpenRecordModal(record);
    }
    if (returnDialog === 'edit') {
      editModal.handleOpenRecordModal(record);
    }
  };

  return (
    <ListPage
      actions={[
        hasResourcePermissions('merchant_accounts', [Permission.Full, Permission.Create]) &&
          (isLoading ? (
            <MerchantsShowActionSkeleton />
          ) : (
            <Button
              key="create"
              label={translate('app.merchantAccounts.add')}
              onClick={() => setOpenCreateDialog(true)}
              startIcon={<ContentAdd />}
              variant="contained"
            />
          )),
      ]}
      disableSyncWithLocation
      empty={false}
      filter={{ merchants: [merchantId] }}
      headerTitle={{
        titleText: translate('app.merchantAccounts.merchantAccountsList'),
        titleSize: 'smallBold',
      }}
      listBoxProps={{ sx: { maxHeight: '100%' } }}
      resource="merchant_accounts"
      sort={{ field: 'id', order: 'DESC' }}
      sx={{ marginTop: 0, marginBottom: 2 }}
    >
      <WithListContext
        render={({ data, isLoading, filterValues }) => {
          const showAlert = !data?.length && !isLoading && !Object.keys(filterValues).length;
          if (showAlert) {
            return (
              <Alert severity="info">
                {translate('app.merchantAccounts.noAccountsForMerchant', { merchantName })}
              </Alert>
            );
          }
          return (
            <>
              {isLoading ? <MerchantsShowFiltersSkeleton /> : <MerchantShowAccountsFilters />}
              {isLoading ? (
                <DatagridSkeleton columnsCount={10} />
              ) : (
                <DatagridStickyContainer>
                  <DatagridStyled bulkActionButtons={false}>
                    <TextField
                      component="div"
                      label="ID"
                      sortBy="id"
                      source="originId"
                      sx={{ marginTop: '2px' }}
                    />
                    {hasResourcePermissions('merchant_accounts', [
                      Permission.Full,
                      Permission.Update,
                    ]) && (
                      <FunctionField
                        cellClassName="column-active"
                        label={EMPTY_FIELD}
                        render={() => <MerchantAccountsNameFieldActiveSwitch />}
                        source="active"
                      />
                    )}
                    <MerchantShowAccountsNameField
                      cellClassName="column-name"
                      label={translate('app.merchantAccounts.name')}
                      onClick={showModal.handleOpenRecordModal}
                      source="name"
                    />
                    <LastActiveField
                      label={translate('app.merchantAccounts.lastUpdated')}
                      merchantId={merchantId}
                      source="balanceUpdatedAtMessage"
                    />
                    <FunctionField
                      label={translate('app.merchantAccounts.accountAccessWithParams')}
                      render={(record: MerchantAccount) =>
                        hasResourcePermissions('merchant_accounts', [
                          Permission.Full,
                          Permission.Update,
                        ]) ? (
                          <Button
                            label={translate('app.merchantAccounts.show')}
                            onClick={() => credentialsListModal.handleOpenRecordModal(record)}
                            size="small"
                            startIcon={<DataObjectIcon />}
                            sx={{ marginTop: '-4px' }}
                          />
                        ) : (
                          <Button
                            disabled
                            label={translate('app.merchantAccounts.noAccess')}
                            size="small"
                            startIcon={<DataObjectIcon />}
                          />
                        )
                      }
                      sortable={false}
                      source="credentials"
                    />
                    <FunctionField
                      label={translate('app.merchantAccounts.partner')}
                      render={(record: MerchantAccount) =>
                        `${record.partner ? translate('app.common.yes') : translate('app.common.no')}`
                      }
                      source="partner"
                    />
                    {hasResourceAccess(Resources.Providers) && (
                      <FunctionField
                        cellClassName="column-provider"
                        label={translate('app.merchantAccounts.provider')}
                        render={(record: any) =>
                          record?.provider ? (
                            <TextFieldStyled
                              onClick={() => providerModal.handleOpenRecordModal(record.provider)}
                              record={record.provider}
                              source="name"
                            />
                          ) : (
                            EMPTY_FIELD
                          )
                        }
                      />
                    )}
                    <FunctionField
                      cellClassName="column-merchantIdEnabled"
                      label={translate('app.merchantAccounts.globalId')}
                      render={({ merchantIdEnabled }: MerchantAccount) =>
                        merchantIdEnabled ? translate('app.common.yes') : translate('app.common.no')
                      }
                      source="merchantIdEnabled"
                    />
                    {hasResourceAccess(Resources.Currencies) && (
                      <FunctionField
                        cellClassName="column-currency"
                        label={translate('app.merchantAccounts.currency')}
                        render={(record: any) =>
                          record?.currency ? (
                            <TooltipOverflow
                              tooltipTitle={`${record.currency.alphaCode}, ${record.currency.name}`}
                            >
                              {record.currency.alphaCode}
                            </TooltipOverflow>
                          ) : (
                            EMPTY_FIELD
                          )
                        }
                      />
                    )}
                    {hasResourceAccess(Resources.Gateways) && (
                      <EmbeddedArrayField
                        cellClassName="column-gateways"
                        label={translate('app.merchantAccounts.gateways')}
                        render={(gateways: Gateway[]) => (
                          <Box display="flex" flexWrap="wrap" gap="4px">
                            {gateways?.map((gateway: Gateway) => (
                              <RecordContextProvider key={gateway.id} value={gateway}>
                                <ReferenceField
                                  link={false}
                                  reference="gateways"
                                  resource="gateways"
                                  source="@id"
                                >
                                  <FunctionField
                                    render={(record) => (
                                      <ChipFieldLinkStyled
                                        color="default"
                                        key={gateway.id}
                                        onClick={() => gatewayModal.handleOpenRecordModal(record)}
                                        record={gateway}
                                        source="name"
                                      />
                                    )}
                                    sx={{ cursor: 'pointer' }}
                                  />
                                </ReferenceField>
                              </RecordContextProvider>
                            ))}
                          </Box>
                        )}
                        sourceField="gateways"
                      />
                    )}
                    <DateTimeField
                      label={translate('app.merchantAccounts.created')}
                      source="createdAt"
                    />
                    <DateTimeField
                      label={translate('app.merchantAccounts.updated')}
                      source="updatedAt"
                    />
                    <ContextMenu
                      render={(record: MerchantAccount) => [
                        hasResourceAccess(Resources.UserActions) && (
                          <ContextMenuEntityUserActionsButton
                            entityName="merchant_account"
                            key="user-action-link"
                          />
                        ),
                        hasResourcePermissions('merchant_accounts', [
                          Permission.Full,
                          Permission.Read,
                        ]) && (
                          <ContextMenuShowDialogButton
                            key="show-button"
                            onClick={() => showModal.handleOpenRecordModal(record)}
                          />
                        ),
                        hasResourcePermissions('merchant_accounts', [
                          Permission.Full,
                          Permission.Update,
                        ]) &&
                          !record?.locked && (
                            <ContextMenuEditDialogButton
                              key="edit-button"
                              onClick={() => editModal.handleOpenRecordModal(record)}
                            />
                          ),
                        hasResourcePermissions('merchant_accounts', [
                          Permission.Full,
                          Permission.Delete,
                        ]) && (
                          <ContextMenuDeleteButton
                            key="delete-button"
                            onClick={() => deleteModal.handleOpenRecordModal(record)}
                          />
                        ),
                      ]}
                    />
                  </DatagridStyled>
                </DatagridStickyContainer>
              )}
            </>
          );
        }}
      />
      {openCreateDialog && (
        <MerchantShowAccountsCreateDialog
          merchantId={merchantId}
          merchantPlatformId={merchantPlatformId}
          onClose={() => setOpenCreateDialog(false)}
          open={openCreateDialog}
        />
      )}
      {editModal.openRecordModal && (
        <MerchantShowAccountsEditDialog
          merchantId={merchantId}
          onClose={editModal.handleCloseRecordModal}
          onDelete={(record: MerchantAccount) => {
            deleteModal.handleOpenRecordModal(record);
            setReturnDialog('edit');
          }}
          open={editModal.openRecordModal}
          record={editModal.record}
        />
      )}
      <MerchantShowAccountsShowDialog
        handleOpenCredentialsDialog={credentialsShowModal.handleOpenRecordModal}
        merchantAccount={showModal.record}
        onClose={showModal.handleCloseRecordModal}
        onDelete={(record: MerchantAccount) => {
          deleteModal.handleOpenRecordModal(record);
          setReturnDialog('show');
        }}
        onEdit={editModal.handleOpenRecordModal}
        open={showModal.openRecordModal}
      />
      {credentialsShowModal.openRecordModal && (
        <AccountsCredentialsDialog
          actions={
            <CardActionsButtons
              rightActionsSlot={[
                <Button
                  key="back-button"
                  label={translate('app.merchantAccounts.view')}
                  onClick={() => {
                    credentialsShowModal.handleCloseRecordModal();
                    showModal.handleOpenRecordModal(credentialsShowModal.record);
                  }}
                  variant="outlined"
                />,
                hasResourcePermissions('merchant_accounts', [
                  Permission.Full,
                  Permission.Update,
                ]) && (
                  <Button
                    label={translate('app.buttons.edit')}
                    onClick={() => {
                      credentialsShowModal.handleCloseRecordModal();
                      editModal.handleOpenRecordModal(credentialsShowModal.record);
                    }}
                    variant="contained"
                  />
                ),
              ]}
            />
          }
          onClose={() => credentialsShowModal.handleCloseRecordModal()}
          open={credentialsShowModal.openRecordModal}
          record={hasAnyRole(READ_ONLY_ROLES) ? {} : credentialsShowModal.record}
        />
      )}
      {credentialsListModal.openRecordModal && (
        <AccountsCredentialsDialog
          actions={
            <CardActionsButtons
              rightActionsSlot={
                hasResourcePermissions('merchant_accounts', [
                  Permission.Full,
                  Permission.Update,
                ]) && (
                  <Button
                    label={translate('app.buttons.edit')}
                    onClick={() => {
                      credentialsShowModal.handleCloseRecordModal();
                      editModal.handleOpenRecordModal(credentialsListModal.record);
                    }}
                    variant="contained"
                  />
                )
              }
            />
          }
          onClose={() => credentialsListModal.handleCloseRecordModal()}
          open={credentialsListModal.openRecordModal}
          record={hasAnyRole(READ_ONLY_ROLES) ? {} : credentialsListModal.record}
        />
      )}
      <MerchantShowAccountsDeleteDialog
        onClose={closeDeleteDialog}
        open={deleteModal.openRecordModal}
        record={deleteModal.record}
      />
      {providerModal.record && (
        <ProviderShowDialog
          onClose={providerModal.handleCloseRecordModal}
          open={providerModal.openRecordModal}
          provider={providerModal.record}
        />
      )}
      {gatewayModal.record && (
        <GatewayShowDialog
          gateway={gatewayModal.record}
          onClose={gatewayModal.handleCloseRecordModal}
          open={gatewayModal.openRecordModal}
        />
      )}
    </ListPage>
  );
};
